// Core
import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";

// Libraries
import NProgress from "nprogress";

// Guards
import requireAuth from "@/router/guards/requireAuth";
import requirePlan from "@/router/guards/requirePlan";
import requireAdmin from "@/router/guards/requireAdmin";
import requireInvite from "@/router/guards/requireInvite";
import requireMeetbase from "@/router/guards/requireMeetbase";
import requireTeamAdmin from "@/router/guards/requireTeamAdmin";
import requireOnboarding from "@/router/guards/requireOnboarding";
import requireLeadMachine from "@/router/guards/requireLeadMachine";
import requireValidSubscription from "@/router/guards/requireValidSubscription";
import requireNoActiveSubscription from "@/router/guards/requireNoActiveSubscription";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    component: () => import("@/components/layout/LayoutComponent.vue"),
    beforeEnter: [requireAuth, requireOnboarding, requirePlan],
    redirect: "/dashboard",
    children: [
      {
        path: "/dashboard",
        name: "dashboard",
        component: () => import(/* webpackChunkName: "dashboard" */ "@/views/HomeView.vue")
      },
      {
        path: "/gerador/chat/:id?",
        name: "chat",
        props: true,
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "chat" */ "@/views/ChatView.vue")
      },
      {
        path: "/biblioteca-de-prompts",
        name: "biblioteca-de-prompts",
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "prompts-library" */ "@/views/prompts/PromptsLibrary.vue")
      },
      {
        path: "/prompt/:id?",
        name: "prompt",
        props: true,
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "prompt-create" */ "@/views/prompts/CreatePrompt.vue")
      },
      {
        path: "/gerador-v2/:id",
        name: "gennai2",
        props: true,
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "gennai" */ "@/views/GennaiView.vue")
      },
      {
        path: "/modelos",
        name: "modelos",
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "models" */ "@/views/models/ModelsView.vue")
      },
      {
        path: "/criar-assistente",
        name: "criar-assistente",
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "custom-models" */ "@/views/models/CustomModelsCreateView.vue")
      },
      {
        path: "/editar-assistente/:id",
        name: "editar-assistente",
        props: true,
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "custom-models" */ "@/views/models/CustomModelsCreateView.vue")
      },
      {
        path: "/gerador-v2/assistentes/:id",
        name: "assistente",
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "custom-models" */ "@/views/models/CustomModelsView.vue")
      },
      {
        path: "/assistentes",
        name: "assistentes",
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "custom-models-list" */ "@/views/models/CustomModelsList.vue")
      },
      {
        path: "/projeto/",
        name: "create-project",
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "project-creation" */ "@/views/project/ProjectCreation.vue")
      },
      {
        path: "/planos/",
        name: "Plans",
        beforeEnter: [requireTeamAdmin],
        component: () => import(/* webpackChunkName: "pricing" */ "@/views/PricingTable.vue")
      },
      {
        path: "/conta",
        name: "account",
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "account" */ "@/views/account/AccountView.vue"),
        children: [
          {
            path: "config",
            name: "account-settings",
            beforeEnter: [requireValidSubscription],
            component: () => import(/* webpackChunkName: "account-settings" */ "@/views/account/UserSettingsView.vue")
          }
        ]
      },
      {
        path: "/configuracoes",
        name: "settings",
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "settings" */ "@/views/account/SettingsView.vue")
      },
      {
        path: "/documentos/",
        name: "Documentos",
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "documents" */ "@/views/docbase/DocumentsList.vue")
      },
      {
        path: "/transcricoes/",
        name: "transcriptions",
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "transcriptions" */ "@/views/transcription/TranscriptionsList.vue")
      },
      {
        path: "/videos/:vslId/edit/:documentId",
        name: "videos",
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "video-edit" */ "@/views/videos/EditTextVSL.vue")
      },
      {
        path: "/escritor/:id?/:chatId?",
        name: "escritor-no-doc",
        props: true,
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "docbase" */ "@/views/docbase/DocbaseView.vue")
      },
      {
        path: "/gerador/vsl/",
        name: "gerador-vsl",
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "video-new" */ "@/views/videos/NewVideo.vue")
      },
      {
        path: "/gerador/artigo/",
        name: "gerador-artigo",
        props: true,
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "document-new" */ "@/views/docbase/NewDocument.vue")
      },
      {
        path: "/gerador/imagem/",
        name: "gerador-imagem",
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "images-generator" */ "@/views/images/ImagesGeneratorView.vue")
      },
      {
        path: "/gerador/transcrever-video",
        name: "transcrever-video",
        props: true,
        beforeEnter: [requireValidSubscription],
        component: () =>
          import(/* webpackChunkName: "video-transcriber" */ "@/views/transcription/VideoTranscriber.vue")
      },
      {
        path: "/atualizar-projeto/",
        name: "update-project",
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "project-update" */ "@/views/project/UpdateProject.vue"),
        children: [
          {
            path: "etapa-1",
            name: "update-project-step1",
            component: () =>
              import(
                /* webpackChunkName: "project-update-steps" */ "@/components/project/projectUpdate/UpdateProjectStep1.vue"
              )
          },
          {
            path: "etapa-2",
            name: "update-project-step2",
            component: () =>
              import(
                /* webpackChunkName: "project-update-steps" */ "@/components/project/projectCreation/steps/ProjectStep2.vue"
              )
          },
          {
            path: "etapa-3",
            name: "update-project-step3",
            component: () =>
              import(
                /* webpackChunkName: "project-update-steps" */ "@/components/project/projectCreation/steps/ProjectStep3.vue"
              )
          },
          {
            path: "etapa-4",
            name: "update-project-step4",
            component: () =>
              import(
                /* webpackChunkName: "project-update-steps" */ "@/components/project/projectCreation/steps/ProjectStep4.vue"
              )
          },
          {
            path: "etapa-5",
            name: "update-project-step5",
            component: () =>
              import(
                /* webpackChunkName: "project-update-steps" */ "@/components/project/projectCreation/steps/ProjectStep5.vue"
              )
          },
          {
            path: "etapa-6",
            name: "update-project-step6",
            component: () =>
              import(
                /* webpackChunkName: "project-update-steps" */ "@/components/project/projectCreation/steps/ProjectStep6.vue"
              )
          },
          {
            path: "etapa-7",
            name: "update-project-step7",
            component: () =>
              import(
                /* webpackChunkName: "project-update-steps" */ "@/components/project/projectCreation/steps/ProjectStep7.vue"
              )
          }
        ]
      },
      {
        path: "/404",
        name: "404",
        component: () => import(/* webpackChunkName: "error" */ "@/views/error/NotFoundView.vue")
      },
      {
        path: "/500",
        name: "500",
        component: () => import(/* webpackChunkName: "error" */ "@/views/error/ServerErrorView.vue")
      },
      {
        path: "/:pathMatch(.*)*",
        redirect: "/404"
      },
      {
        path: "/equipe/:id",
        name: "team",
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "team" */ "@/views/team/TeamView.vue")
      },
      {
        path: "/equipe/:id/projetos/",
        name: "project-list",
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "project-list" */ "@/views/project/ProjectList.vue")
      },
      {
        path: "/equipe/:id/projetos/historico",
        name: "folders-list",
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "folders" */ "@/views/project/FoldersView.vue")
      },
      {
        path: "/equipe/:id/projetos/favoritos",
        name: "folders-list-favorite",
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "folders" */ "@/views/project/FoldersView.vue")
      },
      {
        path: "/universidade",
        name: "universidade",
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "university" */ "@/views/university/UniversityView.vue")
      },
      {
        path: "/universidade/:category",
        name: "universidade-category",
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "university" */ "@/views/university/UniversityCategory.vue")
      },
      {
        path: "/universidade/:category/:video",
        name: "universidade-video",
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "university" */ "@/views/university/UniversityVideo.vue")
      },
      {
        path: "/transcrever-call",
        name: "transcrever-call",
        beforeEnter: [requireValidSubscription, requireMeetbase],
        component: () => import(/* webpackChunkName: "meetbase" */ "@/views/meetbase/SubmitFile.vue")
      },
      {
        path: "/gravacoes",
        name: "calls-list",
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "meetbase" */ "@/views/meetbase/CallsList.vue")
      },
      {
        path: "/templates-de-reuniao",
        name: "meeting-templates",
        beforeEnter: [requireValidSubscription, requireMeetbase],
        component: () => import(/* webpackChunkName: "meetbase" */ "@/views/meetbase/MeetingTemplates.vue")
      },
      {
        path: "/resultado-gravacao/:id",
        name: "call-result",
        beforeEnter: [requireValidSubscription, requireMeetbase],
        component: () => import(/* webpackChunkName: "meetbase" */ "@/views/meetbase/CallResult.vue")
      },
      {
        path: "/maquina-de-leads",
        name: "leads-machine",
        beforeEnter: [requireValidSubscription, requireLeadMachine],
        component: () => import(/* webpackChunkName: "leads" */ "@/views/leads/LeadsRobotView.vue")
      },
      {
        path: "/listas-de-leads",
        name: "leads",
        beforeEnter: [requireValidSubscription, requireLeadMachine],
        component: () => import(/* webpackChunkName: "leads" */ "@/views/leads/LeadsListing.vue")
      },
      {
        path: "/listas-de-leads/:listId/:leadId?",
        name: "leads-list",
        beforeEnter: [requireValidSubscription, requireLeadMachine],
        component: () => import(/* webpackChunkName: "leads" */ "@/views/leads/LeadsList.vue")
      },
      {
        path: "/automacoes",
        name: "automations",
        beforeEnter: [requireValidSubscription, requireLeadMachine],
        component: () => import(/* webpackChunkName: "leads-automations" */ "@/views/leads/AutomationsListing.vue")
      },
      {
        path: "/automacoes-historico",
        name: "automations-history",
        beforeEnter: [requireValidSubscription, requireLeadMachine],
        component: () => import(/* webpackChunkName: "leads-automations" */ "@/views/leads/AutomationsHistory.vue")
      },
      {
        path: "/vendedores",
        name: "sellers",
        beforeEnter: [requireValidSubscription, requireLeadMachine],
        component: () => import(/* webpackChunkName: "leads" */ "@/views/leads/SellersListing.vue")
      },
      {
        path: "/integracoes",
        name: "integrations",
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "integrations" */ "@/views/integrations/IntegrationsListing.vue")
      },
      {
        path: "/integracoes/:id",
        name: "integration-details",
        beforeEnter: [requireValidSubscription],
        component: () => import(/* webpackChunkName: "integrations" */ "@/views/integrations/IntegrationDetails.vue")
      }
    ]
  },
  {
    path: "/f1a3b5c738c9d2f62bac4f37e16d58e9",
    name: "base-panel",
    beforeEnter: [requireAdmin],
    component: () => import(/* webpackChunkName: "admin" */ "@/components/layout/LayoutAdminComponent.vue"),
    children: [
      {
        path: "",
        name: "home-base-panel",
        component: () => import(/* webpackChunkName: "admin" */ "@/views/admin/AdminViewUpdated.vue")
      },
      {
        path: "team/:id",
        name: "admin-team-info",
        component: () => import(/* webpackChunkName: "admin" */ "@/views/admin/AdminTeamInfoView.vue")
      },
      {
        path: "history/:id",
        name: "admin-history",
        component: () => import(/* webpackChunkName: "admin" */ "@/views/admin/AdminHistoryView.vue")
      }
    ]
  },
  {
    path: "/",
    component: () => import(/* webpackChunkName: "auth" */ "@/views/auth/AuthView.vue"),
    children: [
      {
        path: "/login",
        name: "login",
        beforeEnter: [requireInvite],
        component: () => import(/* webpackChunkName: "auth" */ "@/views/auth/LoginView.vue")
      },
      {
        path: "/password-reset",
        name: "password-reset",
        component: () => import(/* webpackChunkName: "auth" */ "@/views/auth/PasswordReset.vue")
      }
    ]
  },
  {
    path: "/sign-up",
    name: "onboarding-cadastro",
    beforeEnter: [requireInvite],
    component: () => import(/* webpackChunkName: "auth" */ "@/views/auth/SignUpOnboarding.vue")
  },
  {
    path: "/subscription",
    name: "subscription",
    beforeEnter: [requireAuth, requireNoActiveSubscription],
    component: () => import(/* webpackChunkName: "auth" */ "@/views/auth/SubscriptionView.vue")
  },
  {
    path: "/trial-iniciado",
    name: "trial-iniciado",
    beforeEnter: [requireAuth],
    component: () => import(/* webpackChunkName: "trial" */ "@/views/project/TrialStarted.vue")
  },
  {
    path: "/resultado-gravacao-publica/:id",
    name: "public-call-result",
    component: () => import(/* webpackChunkName: "meetbase" */ "@/views/meetbase/PublicView.vue")
  },
  {
    path: "/",
    beforeEnter: [requireAuth],
    component: () => import(/* webpackChunkName: "auth" */ "@/views/auth/AuthView.vue"),
    children: [
      {
        path: "/google-auth",
        name: "google-auth",
        component: () => import(/* webpackChunkName: "auth" */ "@/views/auth/GoogleAuth.vue")
      },
      {
        path: "/instrucoes-meetbase",
        name: "meetbase-instructions",
        component: () => import(/* webpackChunkName: "meetbase" */ "@/views/meetbase/MeetbaseHelp.vue")
      }
    ]
  }
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  }
});

router.beforeEach((to, _from, next) => {
  if (to.path.includes("atualizar-projeto")) {
    to.meta.reuseComponent = true;
  }

  NProgress.start();

  next();
});

router.afterEach(() => {
  NProgress.done();
});

export default router;
